// Wochentags-Begrüßungen für das Dashboard.
//
// Aufbau: eine rotierende persönliche Anrede ("Hi {name}!", "Hallo {name}!",
// "Grüß dich {name}!") plus ein witziger, wochentagsspezifischer Satz
// (Agentur-/Branding-Kontext). Der Satz selbst enthält keinen Namen mehr, damit
// die Anrede sauber vorangestellt werden kann.
//
// Auswahl ist *pro Tag deterministisch*: die Anrede wechselt täglich, der
// Wochentags-Satz wöchentlich (jeden Montag ein anderer). Dadurch ändert sich
// der Text innerhalb eines Tages nicht bei jedem Re-Render, rotiert über die
// Wochen aber durch.

const SALUTATIONS = ['Hi {name}', 'Hallo {name}', 'Grüß dich {name}'];

// getDay(): 0 = Sonntag, 1 = Montag, … 6 = Samstag
const LINES = {
  1: [ // Montag
    'Die Woche ist wie ein leeres Artboard – heute legen wir das erste Layer an. ☕',
    'Der Kaffee lädt noch, aber deine Kreativität ist schon im Vollbildmodus.',
    'Das Wochenende war nur ein kurzer Zwischenexport. Jetzt rendern wir die echte Woche.',
    'Neue Woche, neues Moodboard. Mal sehen, was die Pantone-Götter heute für uns bereithalten.',
    'Montagmorgen: erst Kaffee, dann Konzept, dann die Welt.',
    'Auf geht’s – die Deadlines sortieren sich nicht von selbst, obwohl das ein schönes Feature wäre.',
    'Heute ist Montag, also offiziell erlaubt, dreimal „nochmal von vorn“ zu sagen.',
    'Das Briefing wartet, der Kaffee dampft, und irgendwo da draußen freut sich ein Kunde auf dein Konzept.',
    'Frisch eingeloggt und schon kreativer als die halbe Branche. Auf eine starke Woche.',
    'Atme ein, atme aus, öffne das erste Tab von siebzehn.',
    'Heute schreiben wir Headlines, die niemand wieder vergisst. Oder zumindest dein Teamlead.',
    'Der frühe Designer fängt das gute Kerning.',
    'Bereit, dem leeren Whiteboard Leben einzuhauchen? Es wartet schon ganz gespannt.',
    'Montag heißt Anfang. Und Anfänge sind unsere Spezialität – wir machen schließlich Konzepte.',
    'Erste Regel des Montags: Es gibt kein Problem, das ein gutes Briefing und zwei Tassen Kaffee nicht lösen.',
  ],
  2: [ // Dienstag
    'Dienstag – der Tag, an dem aus „Idee“ langsam „Datei“ wird.',
    'Der Montag hat das Eis gebrochen, heute schwimmen wir richtig los.',
    'Jetzt läuft die Maschine warm. Zeit, das gute Zeug zu produzieren.',
    'Heute kein Aufwärmen mehr – direkt ins Konzept, volle Auflösung.',
    'Der Dienstag ist der heimliche Held der Woche. Genau wie ein gut gesetzter Zeilenabstand.',
    'Gestern geplant, heute umgesetzt. Wir lieben so eine Dramaturgie.',
    'Der Kaffee weiß schon, wie du tickst. Die Kunden noch nicht ganz – arbeiten wir dran.',
    'Dienstag: produktiv, fokussiert und nur leicht koffeinabhängig.',
    'Lust auf eine Headline, die sitzt wie ein perfekt platziertes Logo?',
    'Heute ist ein guter Tag, um „weniger ist mehr“ tatsächlich umzusetzen.',
    'Die Ideen von gestern haben über Nacht gegärt – ganz wie ein gutes Konzept (oder ein Sauerteig).',
    'Dienstag-Modus aktiviert. Kreativität: 100 %. Geduld mit Feedbackschleifen: in Arbeit.',
    'Zweiter Tag, zweiter Anlauf, doppelte Pixelpräzision.',
    'Lass uns heute etwas bauen, das auch ohne Erklärungstext funktioniert.',
    'Der Dienstag mag unscheinbar sein – aber so war die erste Skizze des größten Brandings auch mal.',
  ],
  3: [ // Mittwoch
    'Bergfest! Die halbe Woche ist gerendert, die andere Hälfte sieht schon vielversprechend aus.',
    'Mittwoch – ab hier geht’s bergab. Im positiven, energetischen Sinne natürlich.',
    'Wochenmitte erreicht. Zeit, kurz zurückzulehnen und das eigene Moodboard zu bewundern.',
    'Mittwoch: weder Anfang noch Ende – die kreativste Grauzone der Woche.',
    'Halbzeit. Der Spielstand: gute Ideen vorne, Deadlines knapp dahinter.',
    'Heute ist Bergfest – feiern wir mit einem aufgeräumten Ebenen-Menü.',
    'Die Woche hat ihren Scheitelpunkt erreicht. Ab jetzt rollt das Konzept fast von allein.',
    'Mittwoch heißt: genug Schwung für Großes, genug Woche für Feinschliff.',
    'Bergfest! Belohne dich mit einem Kaffee und einer Headline, die endlich sitzt.',
    'Mittwoch ist wie der goldene Schnitt der Woche – genau richtig in der Mitte.',
    'Halbzeit! Zwei Tage Ideen gesammelt, drei Tage Zeit, sie glänzen zu lassen.',
    'Heute weder Montagsfrust noch Freitagseile – nur reiner, fokussierter Flow.',
    'Der Mittwoch grüßt zurück. Auf zur produktivsten Wochenhälfte.',
    'Bergfest-Regel: Eine gute Idee pro Tasse Kaffee. Du bist bei … wie vielen?',
    'Die Woche kippt zu unseren Gunsten. Jetzt nur nicht das gute Layout verwackeln.',
  ],
  4: [ // Donnerstag
    'Donnerstag – der kleine Freitag, nur mit mehr To-dos.',
    'Fast geschafft! Noch zwei Folien Woche, dann kommt der große Export ins Wochenende.',
    'Donnerstag heißt: letzte Feinabstimmung, bevor wir am Freitag den Haken setzen.',
    'Heute den Endspurt einläuten – aber bitte mit sauberem Kerning.',
    'Die Woche neigt sich, die Ideen aber noch lange nicht.',
    'Der Freitag winkt schon von weitem. Erst aber noch eine Runde Brillanz.',
    'Fast am Ziel. Wie ein Konzept kurz vor dem „Ja, genau so!“ des Kunden.',
    'Donnerstag ist der Tag, an dem aus „läuft“ ein „läuft richtig gut“ wird.',
    'Noch ein Tag bis Feierabend-Feeling. Bringen wir das Layout über die Ziellinie.',
    'Donnerstag-Energie: ein Auge auf der Deadline, das andere auf dem Wochenende.',
    'Der kleine Freitag grüßt. Heute noch glänzen, morgen schon entspannen.',
    'Heute die losen Enden verknüpfen – elegant wie eine gute Bildwelt.',
    'Donnerstag ist Beweis genug: Durchhalten lohnt sich, das Wochenende rendert bereits.',
    'Fast da! Noch ein bisschen Politur, dann steht das Wochen-Mockup.',
    'Der Donnerstag ist der Probelauf fürs Wochenende. Lass ihn gut aussehen.',
  ],
  5: [ // Freitag
    'Freitag! Die Woche ist im Kasten – jetzt nur noch den Feinschliff exportieren.',
    'Heute Headlines fertig, Kaffee aus, Wochenende an.',
    'Freitag heißt: einmal alles speichern, was diese Woche schön war.',
    'Endspurt! Noch ein paar Stunden, dann ist Deadline-freie Zone.',
    'Freitag – der Tag, an dem selbst die Feedbackschleifen müde werden.',
    'Lass uns die Woche abrunden wie eine perfekt kuratierte Bildwelt.',
    'Das Wochenende lädt schon im Hintergrund. Nur noch der letzte Klick auf „fertig“.',
    'Freitag: Zeit, stolz auf das eigene Moodboard der Woche zu sein.',
    'Heute keine neuen Großprojekte – heute feiern wir die abgeschlossenen.',
    'Letzter Arbeitstag der Woche: Brillanz mit Blick auf die Uhr.',
    'Freitag ist wie der finale Reinzeichnungs-Check – einmal drüberschauen, dann loslassen.',
    'Die Woche bekommt heute ihren Abschluss-Slide. Mach ihn schön.',
    'Fast geschafft! Noch ein Espresso, noch ein Haken, dann Feierabend.',
    'Freitag: Ideen sichern, Kopf leeren, Wochenende öffnen.',
    'Schönen Freitag! Du hast diese Woche mehr gewuppt als ein ganzer Stockfoto-Katalog.',
  ],
  6: [ // Samstag
    'Samstag? Respekt. Entweder brennt was – oder du brennst für etwas. Beides okay.',
    'Wochenende und trotzdem hier. Die wahre Hingabe eines Kreativen.',
    'Am Samstag entstehen oft die besten Ideen – meist ungeplant und ohne Briefing.',
    'Heute zählt jede Minute doppelt. Mach was Schönes draus.',
    'Samstag-Modus: kein Meeting, kein Lärm, nur du und die gute Idee.',
    'Falls du am Wochenende arbeitest, dann hoffentlich an etwas, das dir Spaß macht.',
    'Samstag ist die heimliche Lieblingszeit vieler Designer – kein Telefon, volle Konzentration.',
    'Heute keine Deadlines, nur Inspiration im Selbstlauf.',
    'Ein Samstag im System? Dann mal her mit dem kreativen Geheimprojekt.',
    'Wochenende heißt: gleiche Leidenschaft, halber Posteingang.',
    'Genieß den Samstag. Auch ein gutes Layout braucht mal Weißraum – und du auch.',
    'Mach langsam, denk weit. Manchmal entsteht das beste Konzept beim Nichtstun.',
  ],
  0: [ // Sonntag
    'Sonntag? Eigentlich Ruhetag – aber wir verraten’s keinem.',
    'Sonntags zählt nur eine Deadline: die fürs Entspannen.',
    'Der Sonntag ist der Weißraum der Woche. Nutz ihn, um durchzuatmen.',
    'Falls du hier bist: Hut ab. Aber denk dran, auch Kreativität braucht Schlaf.',
    'Sonntag: heute keine Pixel schieben – höchstens Gedanken sortieren.',
    'Lehn dich zurück. Die beste Vorbereitung auf Montag ist ein freier Kopf.',
    'Am Sonntag dürfen Ideen einfach reifen – ganz ohne Export-Button.',
    'Sonntag ist Inspirationstag. Spazieren, schauen, staunen – das Briefing kann warten.',
    'Heute gilt: Augen zu und Moodboard im Kopf entstehen lassen.',
    'Sonntag heißt Pause. Und Pausen sind kein Bug, sondern ein Feature.',
    'Der Sonntag grüßt entspannt. Lade die Akkus – morgen wird wieder gerendert.',
    'Ruh dich aus, träum groß. Die besten Konzepte beginnen oft als halbe Idee am Sonntagnachmittag.',
  ],
};

// Lokale Tageszahl seit Epoch – unabhängig von der Uhrzeit/Zeitzone stabil.
function dayNumber(date) {
  return Math.floor((date.getTime() - date.getTimezoneOffset() * 60000) / 86400000);
}

// Liefert die Begrüßung für den Wochentag: rotierende Anrede + Wochentags-Satz,
// mit eingesetztem Namen. Stabil pro Tag, rotiert über die Wochen.
export function weekdayGreeting(name = 'du', date = new Date()) {
  const day = dayNumber(date);
  const lines = LINES[date.getDay()];
  // Anrede wechselt täglich, der Spruch wöchentlich (jeder Wochentag kehrt im
  // 7-Tage-Takt wieder, daher Wochenindex über die Tageszahl).
  const salutation = SALUTATIONS[((day % SALUTATIONS.length) + SALUTATIONS.length) % SALUTATIONS.length];
  const weekIndex = Math.floor(day / 7);
  const line = lines[((weekIndex % lines.length) + lines.length) % lines.length];
  return `${salutation}! ${line}`.replace(/\{name\}/g, name);
}
